import { useMemo } from 'react';
import { useLocation, useOutlet } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Box, Grid, Stack, Typography, useTheme, alpha } from '@mui/material';
import useResponsive from '../../hooks/useResponsive';
import useLocales from '../../hooks/useLocales';
import ReachLinkLogo from '../../components/Logo';
import Carousel from '../../components/carousel/Carousel';
import { FONT_TERTIARY } from '../../theme/typography';

type CarouselItem = {
    image: string;
    title: string;
    description: string;
};

const AuthLayout = () => {
    const { translate } = useLocales();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const location = useLocation();
    const outlet = useOutlet();

    const isDesktop = useResponsive('up', 'md');

    const carouselItems: CarouselItem[] = useMemo(
        () => [
            {
                image: '/assets/illustrations/perfect-therapist.svg',
                title: translate('pages.login.carousel.perfectTherapist.title'),
                description: translate('pages.login.carousel.perfectTherapist.description'),
            },
            {
                image: '/assets/illustrations/unlock-wellness-tools.svg',
                title: translate('pages.login.carousel.unlockWellnessTools.title'),
                description: translate('pages.login.carousel.unlockWellnessTools.description'),
            },
            {
                image: '/assets/illustrations/collaborate-and-grow.svg',
                title: translate('pages.login.carousel.collaborateAndGrow.title'),
                description: translate('pages.login.carousel.collaborateAndGrow.description'),
            },
        ],
        [translate]
    );

    return (
        <Grid container sx={{ minHeight: '100vh', position: 'relative' }}>
            {isDesktop && (
                <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{
                        display: 'flex',
                        overflow: 'hidden',
                        backgroundColor: alpha(theme.palette.primary.lighter, isDark ? 0.15 : 1),
                    }}
                >
                    <Box
                        component={motion.img}
                        src="/assets/illustrations/login-corner-glow.svg"
                        alt=""
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1, scale: [1, 1.2, 1] }}
                        transition={{
                            opacity: { duration: 1.2, ease: 'easeOut' },
                            scale: {
                                duration: 8,
                                repeat: Infinity,
                                ease: 'easeInOut',
                                delay: 1.2,
                            },
                        }}
                        sx={{
                            position: 'absolute',
                            top: -400,
                            left: -400,
                            width: 800,
                            height: 800,
                            pointerEvents: 'none',
                            zIndex: 0,
                        }}
                    />
                    <Stack sx={{ py: 10, flex: 1, zIndex: 1 }}>
                        <Stack
                            component={motion.div}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5, delay: 0.5, ease: 'easeOut' }}
                            sx={{ px: 5 }}
                        >
                            <ReachLinkLogo />
                        </Stack>

                        <Box
                            component={motion.div}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5, ease: 'easeOut', delay: 0.8 }}
                        >
                            <Carousel
                                list={carouselItems.map((item) => (
                                    <Stack
                                        key={item.title}
                                        sx={{
                                            height: 'calc(100vh - 220px)',
                                            pb: 10,
                                        }}
                                    >
                                        <Stack
                                            sx={{
                                                height: 1,
                                                justifyContent: 'space-between',
                                                gap: 4,
                                                px: 5,
                                            }}
                                        >
                                            <Stack
                                                sx={{
                                                    flex: 1,
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Box
                                                    component="img"
                                                    src={item.image}
                                                    alt={item.title}
                                                    sx={{ width: 1, maxWidth: 600 }}
                                                />
                                            </Stack>
                                            <Stack sx={{ gap: 1 }}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_TERTIARY,
                                                        fontSize: 32,
                                                    }}
                                                >
                                                    {item.title}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    sx={{ color: 'text.secondary' }}
                                                >
                                                    {item.description}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                ))}
                                speed={500}
                                autoplaySpeed={8000}
                                showArrows={false}
                                sx={{ flex: 1, width: '50vw' }}
                            />
                        </Box>
                    </Stack>
                </Grid>
            )}

            <Grid item xs={12} md={6}>
                <AnimatePresence mode="wait" initial={true}>
                    <Stack key={location.pathname} height={1} sx={{ position: 'relative' }}>
                        {outlet}
                    </Stack>
                </AnimatePresence>
            </Grid>
        </Grid>
    );
};

export default AuthLayout;
